import { SortingState } from '@tanstack/react-table';
import { SortConfig } from '@/types';
import { COLUMN_TO_BACKEND_FIELD } from '@/hooks/useTranscriptsFilters';

/**
 * Translate a TanStack Table SortingState into the backend's sort_by /
 * sort_type query params. Column IDs that need a different backend field
 * name live in COLUMN_TO_BACKEND_FIELD (shared with the filter pipeline
 * so sort + filter stay in lockstep). IDs not in the map pass through
 * unchanged — the backend validates sort_by against a whitelist regex
 * (see analysis.types.ts GetConversationsDTO) and returns 400 for any
 * non-whitelisted value, so a typo fails loudly rather than silently.
 *
 * `overrides` lets a specific view remap a column id to a different wire
 * field without changing the column's id (and therefore without breaking
 * columnVisibility / the View menu, which are keyed by column id). It is
 * checked before COLUMN_TO_BACKEND_FIELD, so it wins on collision. Used by
 * the Screened Out transcripts view, whose rows have no `startedAt` (they
 * never started an interview) — the "Start time" column keeps id
 * `startedAt` everywhere, but that view sorts on `createdAt` on the wire.
 */
export function sortingStateToSortConfig(
  sorting: SortingState,
  overrides?: Record<string, string>,
): SortConfig {
  if (!sorting.length) return {};
  const [{ id, desc }] = sorting;
  return {
    sort_by: overrides?.[id] ?? COLUMN_TO_BACKEND_FIELD[id] ?? id,
    sort_type: desc ? 'desc' : 'asc',
  };
}
